import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tabs, Badge } from 'antd';
import OrderForm from './OrderForm';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getOrCreateStore,
  createTab as createDraftTab,
  removeTab as removeDraftTab,
  setActiveTab as setActiveDraftTab,
  isPristineDraft,
  OrderDraftInit,
  OrderDraftStore,
} from '@app/services/orderDraft.service';
import './NewOrder.css';

const tabLabel = (label: string) => (
  <span className="tab-label tw-flex tw-items-center tw-gap-2 tw-text-sm">
    <FileTextOutlined />
    {/* Ẩn chữ trên màn hình hẹp, chỉ giữ icon để tab không tràn. */}
    <span className="tw-hidden md:tw-inline">{label}</span>
  </span>
);

const NewOrder = () => {
  // Khôi phục các đơn tạm đã lưu (nếu có), luôn đảm bảo tối thiểu 1 tab.
  const [store, setStore] = useState<OrderDraftStore>(() => getOrCreateStore());

  const navigate = useNavigate();
  const location = useLocation();

  // "Tạo đơn nhanh" (vd từ hộp thư) gửi khách + kênh qua router state → mở tab đơn tạm mới đã
  // điền sẵn rồi xoá state (F5 không tạo thêm tab). Tab đang mở mà còn trống thì thay luôn
  // (không để lại "Đơn hàng N" rỗng). Nhớ theo location.key: chống chạy hai lần ở StrictMode
  // nhưng lần "Tạo đơn nhanh" sau vẫn được xử lý.
  const handledPrefillKey = useRef<string | null>(null);
  useEffect(() => {
    const prefill = (location.state as { orderPrefill?: OrderDraftInit } | null)?.orderPrefill;
    if (!prefill || handledPrefillKey.current === location.key) return;
    handledPrefillKey.current = location.key;
    const current = getOrCreateStore();
    if (current.order.length > 0 && isPristineDraft(current.drafts[current.activeKey])) {
      removeDraftTab(current.activeKey);
    }
    const { store: next } = createDraftTab(undefined, prefill);
    setStore(next);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, location.pathname, location.state, navigate]);

  const items = useMemo(
    () =>
      store.order.map((key) => ({
        label: tabLabel(store.drafts[key]?.label ?? `Đơn hàng ${key}`),
        children: <OrderForm tabKey={key} />,
        key,
      })),
    [store],
  );

  const onChange = (newActiveKey: string) => {
    setActiveDraftTab(newActiveKey);
    setStore((prev) => ({ ...prev, activeKey: newActiveKey }));
  };

  const add = () => {
    const { store: next } = createDraftTab();
    setStore(next);
  };

  const remove = (targetKey: string) => {
    const next = removeDraftTab(targetKey);
    // Nếu vừa đóng tab cuối cùng, tạo lại 1 tab trống để màn hình không rỗng.
    setStore(next.order.length > 0 ? next : getOrCreateStore());
  };

  const onEdit = (
    e: string | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      add();
    } else if (typeof e === 'string') {
      remove(e);
    }
  };

  const operations = (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-1.5 md:tw-flex-row md:tw-items-center md:tw-gap-3">
      <Badge count={store.order.length} showZero color="var(--primary-color)">
        <span className="tw-mr-2 tw-text-[13px] tw-text-on-surface-variant">Tổng đơn hàng</span>
      </Badge>
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
        Quay lại
      </Button>
    </div>
  );

  return (
    <div className="tw-min-h-screen tw-bg-surface-subtle">
      <Tabs
        type="editable-card"
        onChange={onChange}
        activeKey={store.activeKey}
        onEdit={onEdit}
        items={items}
        tabBarExtraContent={operations}
        className="professional-tabs"
        size="large"
        destroyInactiveTabPane
      />
    </div>
  );
};

export default NewOrder;
